import { isProfileUnlocked } from '@/components/plan-locks/creator-locks';
import type {
  PlanScopedContactIdentityResolution,
  PlanScopedContactSummaryDto,
} from '@fluenceur/types';
import type { TFunction } from 'i18next';
import { deriveContactPrimaryAction } from './derive-contact-primary-action';
import { buildContactProfileRow } from './profile/map-creator-profile-to-contact';
import type { ContactCollabRow, ContactShell, ContactProfile } from './contact-types';
import { getCanonicalConversationId } from './canonical-conversation';

/**
 * Builds the drawer's contact object from the resolved identity header plus the
 * aggregate summary slice.
 *
 * The header (name / avatar) comes from `resolved`; everything the rail needs to
 * be truthful — which social profiles exist, the affiliation standing, the live
 * partnership-ad count and the primary CTA — comes from `summary`. Affiliation
 * detail stays empty and is filled by each view's own live slice. Both `resolved`
 * and `summary` may be `undefined` while their queries load: the shell renders
 * empty and fills in on settle; nothing is fabricated.
 *
 * `collabsEnabled` / `partnershipAdsEnabled` gate the two feature-flagged rows
 * (COLLABS and PARTNERSHIP_ADS_BOOST respectively). They are passed in rather than
 * read here so this stays a pure function; the caller resolves the flags and
 * passes `false` while their query loads, so a flagged row never flashes for an
 * org that lacks the feature.
 */
export const buildContactShell = (
  resolved: PlanScopedContactIdentityResolution | undefined,
  summary: PlanScopedContactSummaryDto | undefined,
  t: TFunction,
  locale: string,
  collabsEnabled: boolean,
  partnershipAdsEnabled: boolean
): ContactShell => {
  const profiles: ContactProfile[] = (summary?.socialProfiles ?? []).map((p) =>
    buildContactProfileRow(p, t, locale)
  );

  const collaborations: ContactCollabRow[] = [];

  // Affiliation row only when the contact is actually an affiliate of this brand.
  if (summary?.affiliation) {
    const { active, activeProgramCount } = summary.affiliation;
    collaborations.push({
      view: 'affiliation',
      id: 'affiliation',
      title: t('contactDrawer.rail.collabRows.affiliation.title'),
      sublabel: active
        ? t('contactDrawer.rail.collabRows.affiliation.activeCount', { count: activeProgramCount })
        : t('contactDrawer.rail.collabRows.affiliation.inactive'),
      icon: 'Link',
      active,
      implemented: true,
    });
  }

  // Contractual collabs (sponsored posts) are offerable only when the org holds
  // the COLLABS feature; the row reads "to propose" until at least one collab is
  // in flight (proposed / viewed / accepted). Grouped with the other
  // collaboration kinds, before Ads.
  if (collabsEnabled) {
    const collabsActiveCount = summary?.collabs?.activeCount ?? 0;
    const collabsActive = collabsActiveCount > 0;
    collaborations.push({
      view: 'collabs',
      id: 'collabs',
      title: t('contactDrawer.rail.collabRows.collabs.title'),
      sublabel: collabsActive
        ? t('contactDrawer.rail.collabRows.collabs.activeCount', { count: collabsActiveCount })
        : t('contactDrawer.rail.collabRows.collabs.propose'),
      icon: 'SendDiagonal',
      active: collabsActive,
      implemented: true,
    });
  }

  // Partnership ads are offerable only when the org holds the boost feature; the
  // row reads "to propose" until an authorization is live.
  if (partnershipAdsEnabled) {
    const adsActiveCount = summary?.ads.activeCount ?? 0;
    const adsActive = adsActiveCount > 0;
    collaborations.push({
      view: 'ads',
      id: 'ads',
      title: t('contactDrawer.rail.collabRows.partnershipAds.title'),
      sublabel: adsActive
        ? t('contactDrawer.rail.collabRows.partnershipAds.activeCount', { count: adsActiveCount })
        : t('contactDrawer.rail.collabRows.partnershipAds.propose'),
      icon: 'Megaphone',
      active: adsActive,
      implemented: true,
    });
  }

  const primary = deriveContactPrimaryAction({
    hasEmail: summary?.hasEmail ?? false,
  });

  // A prospect the plan masks has no name to show: it reads "Hidden creator".
  const locked = resolved?.locked ?? summary?.locked;
  const unlocked = isProfileUnlocked(resolved) || isProfileUnlocked(summary);

  return {
    identityId: resolved?.creatorIdentityId ?? '',
    name: resolved?.displayName ?? (locked?.identity ? t('planLocks.name') : ''),
    avatarUrl: resolved?.avatarUrl ?? '',
    primary,
    activeIgConversationId: summary?.activeIgConversationId ?? null,
    canonicalConversationId: summary ? getCanonicalConversationId(summary) : null,
    profiles,
    collaborations,
    contact: {
      addedOn: summary?.addedOn ?? '',
      automaticMessages: summary?.automaticMessages,
    },
    affiliation: { performance: [], programs: [] },
    ...(locked && { locked }),
    ...(unlocked && { unlocked: true as const }),
  };
};
